import { DestroyRef, inject, Injectable } from "@angular/core";
import { ProjectService } from "@domain/project/project.service";
import { ErrorReportingService } from "@utils/error-reporting.service";
import { catchError, EMPTY, filter, fromEvent, merge, mergeMap } from "rxjs";

/**
 * Triggers a cloud sync when the app is likely to be backgrounded or closed, and when network connectivity
 * returns. The periodic 60 s timer in {@link ProjectService} alone is not enough for a PWA: the browser/OS
 * freezes timers once the app is in the background, so a user who edits a project and immediately switches
 * away from - or closes - the PWA can lose up to 60 s of otherwise-pending sync.
 *
 * `pagehide` in particular is best-effort: the operating system can terminate the process before the
 * triggered HTTP request completes. The actual safety net against data loss is the local persistence from
 * Block 1 (`ResilientIndexedDbService`) plus the correct dirty flag from Block 2 (`lastCloudSync` is only
 * written once the asset sync is confirmed) - this service only makes a successful sync more likely to
 * happen before the app disappears, it cannot guarantee it.
 *
 * All triggers share the same in-flight guard via {@link ProjectService.syncNow}, so this service can never
 * run a sync concurrently with the periodic timer or another lifecycle trigger.
 */
@Injectable({ providedIn: "root" })
export class AppLifecycleService {
  private projectService = inject(ProjectService);
  private errorReportingService = inject(ErrorReportingService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    const documentHidden$ = fromEvent(document, "visibilitychange").pipe(filter(() => document.visibilityState === "hidden"));
    const pageHide$ = fromEvent(window, "pagehide");
    const online$ = fromEvent(window, "online");

    // mergeMap (not switchMap/concatMap): a later trigger must never cancel a sync already in flight from an
    // earlier one. syncNow() already de-duplicates concurrent calls, so this never starts the sync twice.
    const subscription = merge(documentHidden$, pageHide$, online$)
      .pipe(
        mergeMap(() =>
          this.projectService.syncNow().pipe(
            catchError((error) => {
              this.errorReportingService.captureException(error, {
                tags: { area: "app-lifecycle-sync" },
              });
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
}
